import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, IconStar, IconUsers } from '../atoms';
import { useFollow, FollowableType } from '../lib/useFollow';
import { useCurrentUser } from '../lib/useCurrentUser';
import { roleLabel } from '../lib/roles';
import { api } from '../lib/api';

/**
 * FollowButton — the shared Follow / Following toggle for entity detail pages
 * (migration 101). Optimistic via useFollow: the label + variant flip instantly
 * on click and roll back if the request fails.
 *
 *   not following → outlined star + "Follow"
 *   following     → filled  star + "Following"  (click again to unfollow)
 *
 * §6 — a SUPER ADMIN also sees a users-icon "N" chip beside the button that
 * reveals WHO follows this record (real names, never emails), on any followable
 * record. Icons are line icons (no emoji — punchlist §4).
 */

export interface FollowButtonProps {
  entityType: FollowableType;
  entityId: string | undefined;
  size?: 'sm' | 'md' | 'lg';
}

interface Follower {
  user_id: string;
  name: string;
  role: string | null;
}

export function FollowButton({ entityType, entityId, size = 'sm' }: FollowButtonProps) {
  const { following, toggle, isLoading } = useFollow(entityType, entityId);
  const currentUser = useCurrentUser();
  const isSuper = currentUser?.role === 'super_admin';
  const [open, setOpen] = useState(false);

  // §6 — the follower roster, super-admin only. Fetched eagerly (when there is an
  // entity) so the chip shows the count without a click; names render on open.
  const followersQuery = useQuery<{ count: number; followers: Follower[] }>({
    queryKey: ['followers', entityType, entityId],
    queryFn: ({ signal }) => api.get<{ count: number; followers: Follower[] }>(`/api/follows/${entityType}/${entityId}/followers`, signal),
    enabled: !!entityId && !!isSuper,
    staleTime: 30_000,
  });
  const followers = followersQuery.data?.followers ?? [];
  const count = followersQuery.data?.count ?? 0;

  return (
    <span style={{ position: 'relative', display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
      <Button
        variant={following ? 'primary' : 'secondary'}
        size={size}
        onClick={toggle}
        disabled={!entityId || isLoading}
        aria-pressed={following}
        title={
          following
            ? 'Following — you get this record’s updates. Click to unfollow.'
            : 'Follow to track this record’s activity + notifications.'
        }
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
          <IconStar size={14} filled={following} />
          {following ? 'Following' : 'Follow'}
        </span>
      </Button>

      {isSuper && (
        <>
          <Button
            variant="ghost"
            size={size}
            onClick={() => setOpen((o) => !o)}
            disabled={!entityId}
            aria-expanded={open}
            title="See who follows this record"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
              <IconUsers size={14} /> {count}
            </span>
          </Button>
          {open && (
            <div
              role="menu"
              aria-label="Followers"
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                minWidth: 220, maxHeight: 300, overflowY: 'auto',
                background: 'var(--color-card)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-lg, 10px)', boxShadow: 'var(--shadow-md, 0 6px 20px rgba(0,0,0,.18))',
                padding: 'var(--sp-2)',
              }}
            >
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', padding: 'var(--sp-1) var(--sp-2)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Followers ({count})
              </div>
              {followers.length === 0 ? (
                <div style={{ padding: 'var(--sp-2)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
                  No one is following this record yet.
                </div>
              ) : (
                followers.map((f) => (
                  <div key={f.user_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', alignItems: 'center', padding: 'var(--sp-1) var(--sp-2)' }}>
                    <span style={{ color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>{f.name}</span>
                    <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>{roleLabel(f.role)}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </span>
  );
}
